import moment from 'moment'
import * as XLSX from 'xlsx'

import { DATE_FORMAT } from '_utils/constant'

const sanitizeFileName = (title) => title.replace(/[^a-z0-9]/gi, '_')

// One entry per subitem-choice group, in the same layout the "Subitems"
// section of the template expects: [index, category, required, list].
// Each product's own cell just lists which indices it uses.
const buildSubItemSection = (products) => {
  const rows = []
  let nextIndex = 1

  const indicesByProduct = products.map((product) =>
    (product.subItems || []).map((group) => {
      const index = nextIndex
      nextIndex += 1
      const list = (group.list || [])
        .map((choice) => `${choice.name}:$${Number(choice.price || 0)}`)
        .join('\n')
      rows.push([index, group.type || group.title || '', group.required ? 1 : 0, list])
      return index
    }))

  return { rows, indicesByProduct }
}

/**
 * Exports an event as the exact template layout `readFileToCreateEvent`
 * (src/utils/function.js) expects on upload - same fixed cell positions as
 * public/misc/sample.xlsx - so downloading here and re-uploading on the
 * "New event" form pre-fills it instead of recreating the event by hand.
 */
export const downloadEventTemplateExcel = (event) => {
  const products = event.products || []
  const closingDateIso = moment(event.closingTime, DATE_FORMAT).format('YYYY-MM-DD')
  const pickupDateIso = moment(event.pickupTime, DATE_FORMAT).format('YYYY-MM-DD')
  const { rows: subItemRows, indicesByProduct } = buildSubItemSection(products)

  const rows = [
    ['EVENT DETAILS'],
    ['Title*', event.title || ''],
    ['Description', event.description || ''],
    ['Pick-up location', event.pickupAddress || ''],
    ['Closing date*', closingDateIso],
    ['Delivery date*', pickupDateIso],
    // readFileToCreateEvent only recognizes the literal string 'TRUE' as "pay later".
    ['Pay later*', event.paynow ? 'FALSE' : 'TRUE'],
    ['Admin fee', event.adminCost || 0],
    ['Image URL', event.photoUrls?.[0] || ''],
    [],
    ['ITEMS', 'Item name', ...products.map((product) => product.name)],
    ['', 'Price', ...products.map((product) => product.price)],
    ['', 'Available stock', ...products.map((product) => product.maxQuantity)],
    ['', 'Image', ...products.map((product) => product.photoUrls?.[0] || '')],
    ['', 'Limit per order', ...products.map((product) => product.limitPerOrder)],
    ['', 'Subitems', ...indicesByProduct.map((indices) => indices.join(','))],
    [],
    ['Subitems'],
    ['', 'Category', 'Require', 'List'],
    ...subItemRows,
  ]

  const worksheet = XLSX.utils.aoa_to_sheet(rows)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')
  XLSX.writeFile(workbook, `${sanitizeFileName(event.title)}_template.xlsx`)
}

// How long to wait after the new tab's initial HTML/JS loads before printing.
// The event detail page still needs to fetch and render its data client-side
// after that, and there's no signal back from that page to know when it's done.
const PRINT_RENDER_DELAY_MS = 1200

/**
 * "Download as PDF" - opens the event's real public detail page (the same
 * UI buyers see) in a new tab and triggers the browser's print dialog, so
 * the exported PDF is exactly that page rather than a separately
 * maintained summary.
 */
export const printEventDetailPage = (eventId) => {
  const url = `${process.env.REACT_APP_APP_URL}/event/detail?eid=${eventId}`
  const detailWindow = window.open(url, '_blank')
  if (!detailWindow) return

  detailWindow.addEventListener('load', () => {
    setTimeout(() => {
      detailWindow.focus()
      detailWindow.print()
    }, PRINT_RENDER_DELAY_MS)
  })
}
